<template>
  <Menu
    v-if="state"
    ref="menuRef"
    v-model="isActive"
    class="refinement-menu"
    :class="[items.length === 0 && 'no-refinement']"
    anchor="top left"
    self="top right"
    :offset="[0, 0]"
    :auto-close="false"
    no-route-dismiss
  >
    <div class="refinement-menu--inner">
      <div class="refinement-menu--header">
        <q-toolbar>
          <q-input
            :model-value="searchForFacetValuesQuery"
            class="refinement-menu--input"
            type="search"
            name="search-input"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            :placeholder="t(`filters:Filter.${camelCase(attribute.split('.')[0])}.select.placeholderFilterable`)"
            :loading="isLoading"
            :debounce="300"
            clear-icon="mono-cross"
            autofocus
            clearable
            hide-bottom-space
            dense
            no-error-icon
            @update:model-value="v => searchForFacetValues = v"
          />
        </q-toolbar>
      </div>

      <div class="refinement-menu--body">
        <div
          v-if="state.isFromSearch && items.length === 0"
          class="refinement-menu--no-results"
        >
          No results.
        </div>

        <q-scroll-area
          v-else
          :id="`scrollarea-${attribute}`"
          ref="scroll-target-ref"
        >
          <q-virtual-scroll
            v-slot="{ item, index }"
            class="refinement-menu--list"
            :scroll-target="`scrollarea-${attribute}`"
            :items="items"
            separator
          >
            <ListItem
              :key="index"
              class="refinement-menu--item"
              tag="label"
              clickable
            >
              <ListSection
                class="checkbox-section"
                side
                top
              >
                <Checkbox
                  :model-value="selected.includes(item.value)"
                  size="xs"
                  :val="item"
                  @update:model-value="refine(item.value)"
                />
              </ListSection>

              <ListSection class="label-section">
                <ListLabel>
                  {{ item.label }}
                </ListLabel>
              </ListSection>

              <ListSection
                class="count-section"
                side
                top
              >
                <Badge
                  class="count"
                  :label="item.count"
                />
              </ListSection>
            </ListItem>
          </q-virtual-scroll>
        </q-scroll-area>
      </div>

      <!-- v-if="selected.length" -->
      <div
        class="refinement-menu--footer"
      >
        <q-toolbar>
          <ClearRefinements>
            <template #default="scope">
              <Button

                size="sm"
                variant="text"
                start-icon="mono-delete"
                :label="t('filters:Tools.clear.label')"
                :disabled="!scope.canRefine"
                @click.prevent="scope.refine"
              >
                <Tooltip type="negative">
                  {{ t('filters:Tools.clear.tooltip') }}
                </Tooltip>
              </Button>
            </template>
          </ClearRefinements>
        </q-toolbar>
      </div>
    </div>
  </Menu>
</template>

<script setup>
import { inject, ref, computed, watch, useTemplateRef } from 'vue'
import { useTranslation } from 'i18next-vue'
import Button from '@/components/ui/Button/Button.vue'
import ClearRefinements from '@/components/instant-search/ClearRefinements.vue'
import { camelCase } from 'lodash'

defineOptions({ name: 'RefinementMenu' })
const isActive = defineModel({ type: Boolean, default: false })
const hasSelected = defineModel('hasSelected', { type: Boolean, default: false })
const props = defineProps({
  attribute: { type: String, required: true },
  // Rendered refinementList connector state, owned by a caller-managed widget
  // (e.g. FiltersMenu's permanent one) — RefinementMenu never mounts its own
  // widget so it can't fight another widget refining the same attribute.
  state: { type: Object, default: null },
  itemsTranslationMap: { type: Object, default: null }
})

const $emitter = inject('instantSearchPanelEmitter', { emit: () => {} })

const { t } = useTranslation()

const scrollTargetRef = useTemplateRef('scroll-target-ref') // eslint-disable-line
const isLoading = ref(false)
const searchForFacetValuesQuery = ref('')
const hasEmitted = ref(false)

const selected = computed(() => props.state?.items?.filter(i => i.isRefined).map(i => i.value) || [])

const searchForFacetValues = computed({
  get: () => {
    return searchForFacetValuesQuery.value
  },
  set: v => {
    props.state?.searchForItems(v)
    searchForFacetValuesQuery.value = v
  }
})

const items = computed(() => {
  return (props.state?.items ?? []).map(item => {
    const label = props.itemsTranslationMap?.[item.label] ?? item.label
    return Object.assign({}, item, {
      _highlightResult: { item: { value: item.highlighted } },
      label
    })
  })
})

const refine = item => {
  isLoading.value = true
  props.state?.refine(item)
  searchForFacetValuesQuery.value = ''
  hasSelected.value = !!selected.value.length
  isLoading.value = false
}

const mapStateToCanRefine = s => !!s.canRefine

const clearSelected = () => {}

watch(() => props.state, (newState, oldState) => {
  if (!newState) return

  const previousCanRefine = mapStateToCanRefine(oldState || {})
  const nextCanRefine = mapStateToCanRefine(newState)

  if (!hasEmitted.value || previousCanRefine !== nextCanRefine) {
    $emitter.emit('PANEL_CHANGE_EVENT', nextCanRefine)
    hasEmitted.value = true
  }
}, { immediate: true })

defineExpose({ clearSelected })
</script>

<style>
@reference "#app-styles.css";

.refinement-menu { @apply overflow-visible; }
</style>

<style scoped>
@reference "#app-styles.css";

  .refinement-menu--no-results {}

  .refinement-menu--inner {
    @apply flex flex-col min-w-70;

    .refinement-menu--header,
    .refinement-menu--footer {
      @apply shrink h-11 edge-float;

      .q-toolbar { @apply h-full px-4; }
    }

    .refinement-menu--header {
      @apply border-b;

      .refinement-menu--input {
        @apply w-full;

        &:deep(.q-field__control)::before { @apply border-none; }
        &:deep(.q-field__control)::after { @apply content-none; }
      }
    }
    .refinement-menu--footer { @apply border-t; }

    .refinement-menu--body {
      @apply grow max-h-80 h-svh;

      .q-scrollarea { @apply h-full; }
      .q-virtual-scroll { @apply h-full; }

      .refinement-menu--list {
        .refinement-menu--item {
          .checkbox-section {
            .checkbox-standard { @apply min-h-3.5; }
          }
        }
      }
    }
  }

</style>
